import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import { Button, Form, Spin, InputNumber, Tabs } from "antd";
import { LoadingOutlined, CloseOutlined } from "@ant-design/icons";
import PaymentMethodItem from "./PaymentMethodItem";

import { createOrderTopup, getPaymentMethodWithFee } from "../../api";
import { commonUtil } from "../../utils";
import * as queryString from "query-string";

import styles from "./wallet.module.scss";
import { useAuth } from "../../contexts/authContext";

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

interface Props {
  handleChangeView: any;
  cancelEmbed: any;
}

const WalletDeposit: React.FC<Props> = ({ handleChangeView, cancelEmbed }) => {
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { t, i18n } = useTranslation(["common", "notification"]);
  const [isEmbed, setIsEmbed] = useState<boolean>(false);
  const [depositData, setDepositData] = useState<any>({
    type: "deposit",
    amount: null,
    paymentMethod: "",
    userType: "person",
  });
  const [paymentMethodListLoading, setPaymentMethodListLoading] =
    useState<boolean>(false);
  const [paymentMethodList, setPaymentMethodList] = useState<any>([]);
  const [amountInput, setAmountInput] = useState<any>(0);
  const [createOrderLoading, setCreateOrderLoading] = useState<boolean>(false);
  const ref = useRef<any>({ inputHtml: "", inputBox: "", firstLoad: true });
  const auth = useAuth();

  useEffect(() => {
    const parseQuery = queryString.parse(search);

    if (parseQuery && parseQuery.amount) {
      setDepositData((prev: any) => ({
        ...prev,
        amount: Number(parseQuery.amount || 0),
      }));
    }

    if (parseQuery && parseQuery.paymentMethod) {
      setDepositData((prev: any) => ({
        ...prev,
        paymentMethod: String(parseQuery.paymentMethod),
      }));
    }

    if (parseQuery && parseQuery.userType) {
      setDepositData((prev: any) => ({
        ...prev,
        userType: String(parseQuery.userType),
      }));
    }

    if (parseQuery && parseQuery.embed) {
      setIsEmbed(parseQuery.embed === "true");
    }

    if (parseQuery && parseQuery.lang) {
      i18n.changeLanguage(parseQuery.lang === "en" ? "en" : "vi");
      console.log("vào", i18n.language);
    }
  }, [pathname, search]);

  useEffect(() => {
    const parseQuery = queryString.parse(search);
    fetchPaymentMethodList(parseQuery?.amount);
  }, [search]);

  const fetchPaymentMethodList = async (amount: any) => {
    try {
      if (ref.current.firstLoad) {
        setPaymentMethodListLoading(true);
      }

      const paymentMethodListData = await getPaymentMethodWithFee({
        request_from: "FE_TOPUP",
        amount: amount || 0,
      });

      if (paymentMethodListData.status === "success") {
        setPaymentMethodList(paymentMethodListData.data);
      }
      if (ref.current.firstLoad) {
        setPaymentMethodListLoading(false);
        ref.current.firstLoad = false;
      }
    } catch (error: any) {
      console.log(JSON.stringify(error.repsonse));
    }
  };

  function handleChangePaymentMethod(paymentMethod: string) {
    const parseQuery = queryString.parse(search);

    const data = {
      ...parseQuery,
      ...depositData,
      paymentMethod: paymentMethod,
    };

    history.push(
      pathname +
        "?" +
        queryString.stringify(data, { skipNull: true, skipEmptyString: true })
    );
  }

  function handleChangeAmount(amount: number | string | undefined | null) {
    const parseQuery = queryString.parse(search);

    const data = {
      ...parseQuery,
      ...depositData,
      amount: amount,
    };

    history.push(
      pathname +
        "?" +
        queryString.stringify(data, { skipNull: true, skipEmptyString: true })
    );
  }

  async function createOrder() {
    if (depositData.amount < 10000) {
      ref.current.inputHtml.focus();
      return commonUtil.showMessage(
        "error",
        t("common:Vui lòng nhập số tiền cần nạp lớn hơn 10000đ")
      );
    } else if (
      auth?.user?.business_role === "agency" &&
      depositData.amount < 5000000
    ) {
      ref.current.inputHtml.focus();
      return commonUtil.showMessage(
        "error",
        // t("common:Tài khoản Đại lý số tiền nạp tối thiểu là 5,000,000vnđ")
        t("notification:flush_up_wallet_deposit_money_minimum_silver_agency", {
          price: "5.000.000",
        })
      );
    } else if (
      auth?.user?.business_role === "general_agency" &&
      depositData.amount < 10000000
    ) {
      ref.current.inputHtml.focus();
      return commonUtil.showMessage(
        "error",
        // t("common:Tài khoản Tổng đại lý số tiền nạp tối thiểu là 8,000,000vnđ")
        t("notification:flush_up_wallet_deposit_money_minimum_gold_agency", {
          price: "10.000.000",
        })
      );
    }

    if (depositData.amount > 100000000) {
      ref.current.inputHtml.focus();
      return commonUtil.showMessage(
        "error",
        "Vui lòng nhập số tiền cần nạp nhỏ hơn 100.000.000đ!"
      );
    }

    if (depositData.paymentMethod === "") {
      return commonUtil.showMessage(
        "error",
        "Vui lòng chọn phương thức thanh toán!"
      );
    }

    // Create order
    setCreateOrderLoading(true);

    const createOrderResponse = await createOrderTopup({
      amount: depositData.amount,
      payment_method: depositData.paymentMethod,
      user_type: depositData.userType,
      booking_platform: "ATD",
    });

    // console.log(createOrderResponse)

    const parseQuery = queryString.parse(search);

    const data = {
      ...parseQuery,
      ...depositData,
      type:
        depositData.paymentMethod === "payment_method_bank_transfer"
          ? "bankTransfer"
          : "paymentConfirmation",
      paymentStatus: "checking",
      orderId: createOrderResponse.data.id,
      orderCode: createOrderResponse.data.order_code,
      gwTransactionId: createOrderResponse.data.gw_transaction_id,
    };

    history.push(
      pathname +
        "?" +
        queryString.stringify(data, { skipNull: true, skipEmptyString: true })
    );

    handleChangeView(
      [
        "payment_method_bank_transfer",
        "payment_method_bank_transfer_new",
      ].includes(depositData.paymentMethod)
        ? "bankTransfer"
        : "paymentConfirmation"
    );
    setCreateOrderLoading(false);
  }
  console.log("depositData.amount", depositData?.amount || 0);
  // RENDERING
  if (paymentMethodListLoading) {
    return (
      <Spin
        size="large"
        indicator={<LoadingOutlined spin />}
        style={{ width: "100%" }}
      />
    );
  }

  return (
    <Spin indicator={antIcon} spinning={createOrderLoading}>
      <Tabs
        defaultActiveKey="1"
        style={{
          borderRadius: "8px",
          padding: "0 16px 16px 16px",
          marginBottom: "16px",
          backgroundColor: "white",
          minHeight: "100vh",
        }}
        // tabBarExtraContent={
        //   isEmbed ? <CloseOutlined onClick={cancelEmbed} /> : null
        // }
      >
        <Tabs.TabPane tab={t("common:Nạp tiền")} key="1">
          <Form layout={"vertical"}>
            <Form.Item
              name={"amount"}
              label={t("common:Bước_1_Nhập_số_tiền_cần_nạp")}
              rules={[
                ({ getFieldValue }) => ({
                  validator() {
                    if (
                      !getFieldValue("amount") ||
                      getFieldValue("amount") <= 0
                    ) {
                      return Promise.reject(new Error(`Vui lòng nhập số tiền`));
                    } else {
                      return Promise.resolve();
                    }
                  },
                }),
              ]}
            >
              <InputNumber
                value={depositData.amount || 0}
                onChange={(value) => {
                  if (ref.current.inputBox) {
                    clearTimeout(ref.current.inputBox);
                  }
                  ref.current.inputBox = setTimeout(() => {
                    handleChangeAmount(value);
                  }, 1000);
                }}
                placeholder={"Ví dụ: 100.000"} ///\B(?=(\d{3})+(?!\d))/g
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
                }
                parser={(value) =>
                  value ? Number(value.replace(/\./g, "")) : undefined
                }
                style={{ width: "50%" }}
                min={0}
                ref={(e) => (ref.current.inputHtml = e)}
              />
            </Form.Item>
            <Form.Item label={t("common:Bước_2_Chọn_phương_thức_thanh_toán")}>
              {paymentMethodList.map((res: any, index: any) => {
                return (
                  <PaymentMethodItem
                    key={index}
                    data={res}
                    handleChangePaymentMethod={handleChangePaymentMethod}
                    selectedPaymentMethod={depositData.paymentMethod}
                  />
                );
              })}
            </Form.Item>
          </Form>

          <Form layout="inline" style={{ marginTop: "16px", float: "right" }}>
            {!isEmbed ? (
              <Form.Item className={styles.walletFormItem}>
                <Button
                  type="default"
                  onClick={() => {
                    history.push(
                      pathname +
                        "?" +
                        queryString.stringify(
                          { type: "overview", lang: i18n.language },
                          { skipNull: true, skipEmptyString: true }
                        )
                    );
                    handleChangeView("overview");
                  }}
                >
                  {t("common:Huỷ")}
                </Button>
              </Form.Item>
            ) : null}

            {isEmbed ? (
              <Form.Item className={styles.walletFormItem}>
                <Button type="default" onClick={cancelEmbed}>
                  {t("common:Huỷ")}
                </Button>
              </Form.Item>
            ) : null}

            <Form.Item
              className={styles.walletFormItem}
              style={{ float: "right" }}
            >
              <Button type="primary" onClick={createOrder}>
                {t("common:Tiếp tục")}
              </Button>
            </Form.Item>
          </Form>
        </Tabs.TabPane>
      </Tabs>
    </Spin>
  );
};

export default WalletDeposit;
